import { doc, updateDoc } from "firebase/firestore";
import { getMessaging, getToken } from "firebase/messaging";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { auth, db } from "../firebase";
import notificationService from "../services/notificationService";

function EditEventModal({ onClose, onEventUpdated, event }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (event) {
      setTitle(event.title || "");
      setDate(event.date || "");
      setTime(event.time || "");
      setDescription(event.description || "");
    }
  }, [event]);

  useEffect(() => {
    // Create style element for calendar and time input icons
    const style = document.createElement("style");
    style.textContent = `
      input[type="date"]::-webkit-calendar-picker-indicator,
      input[type="time"]::-webkit-calendar-picker-indicator {
        filter: invert(1);
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Generate timestamp from date and time
  const generateTimestamp = (dateStr, timeStr) => {
    try {
      const dateTimeString = `${dateStr}T${timeStr || "00:00"}:00`;
      const newDate = new Date(dateTimeString);

      if (isNaN(newDate.getTime())) {
        console.error(
          "Error generating timestamp: Invalid date/time string",
          dateTimeString
        );

        const fallbackDate = new Date(dateStr);
        return fallbackDate.getTime();
      }

      console.log(
        `Generated timestamp for ${dateStr} ${timeStr}: ${newDate.getTime()} (${newDate.toString()})`
      );
      return newDate.getTime();
    } catch (e) {
      console.error("Error generating timestamp:", e);
      return new Date(dateStr).getTime();
    }
  };

  // Format time string
  const formatTimeString = (timeStr) => {
    if (!timeStr) return "";
    try {
      const [hours, minutes] = timeStr.split(":");
      if (isNaN(hours) || isNaN(minutes)) return timeStr;
      const date = new Date();
      date.setHours(parseInt(hours, 10));
      date.setMinutes(parseInt(minutes, 10));
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      console.error("Error formatting time:", e, "Input:", timeStr);
      return timeStr;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate form
      if (!title.trim()) {
        setError("Title is required");
        setLoading(false);
        return;
      }

      if (title.trim().length > 100) {
        setError("Title should be less than 100 characters");
        setLoading(false);
        return;
      }

      if (!date) {
        setError("Date is required");
        setLoading(false);
        return;
      }

      if (!time) {
        setError("Time is required");
        setLoading(false);
        return;
      }

      if (description && description.length > 100) {
        setError("Description should be less than 100 characters");
        setLoading(false);
        return;
      }

      // Check if event and event.id exist and are valid
      if (!event || !event.docId) {
        setError("Invalid event reference");
        setLoading(false);
        return;
      }

      // Get current user ID
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setError("User not authenticated");
        setLoading(false);
        return;
      }

      console.log("Updating event with ID:", event.docId);

      // Get FCM token for notifications
      let fcmToken = null;
      try {
        const messaging = getMessaging();
        fcmToken = await getToken(messaging, {
          vapidKey:
            "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
        });
        console.log("FCM token obtained:", fcmToken);
      } catch (error) {
        console.error("Error getting FCM token:", error);
        console.warn(
          "Could not get FCM token. Push notifications might not work.",
          error.message
        );
      }

      // Generate timestamp for the event
      const timestamp = generateTimestamp(date, time);
      if (isNaN(timestamp)) {
        setError("Invalid date or time entered");
        setLoading(false);
        return;
      }

      // Check if event date is in the future to determine if it's scheduled
      const eventDate = new Date(timestamp);
      const isScheduled = eventDate > new Date();

      // Prepare updated event data
      const updatedData = {
        title: title.trim(),
        date: date,
        time: formatTimeString(time),
        time1: timestamp,
        fcmToken: fcmToken || null,
        description: description.trim(),
        isScheduled: isScheduled,
        status: "updated",
        lastUpdated: new Date().toISOString(),
      };

      // Update the document in Firestore
      const eventRef = doc(db, "lawyers", uid, "events", event.docId);
      await updateDoc(eventRef, updatedData);

      toast.success("Event updated successfully");
      onEventUpdated(); // Refresh events list
      onClose(); // Close the modal
    } catch (err) {
      console.error("Error updating event:", err);
      setError("Failed to update event: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-10 px-4 sm:px-0">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      <div className="bg-[#060125] rounded-lg p-4 sm:p-6 w-full max-w-[400px] z-50">
        <h2 className="text-lg sm:text-xl font-bold text-white text-center mb-4 sm:mb-6">
          Edit Event
        </h2>
        {error && (
          <p className="text-red-500 text-center mb-4 text-sm">{error}</p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title Input */}
          <div>
            <input
              type="text"
              placeholder="Enter Event Title"
              value={title}
              onChange={(e) => {
                const text = e.target.value.slice(0, 20);
                const capitalized =
                  text.charAt(0).toUpperCase() + text.slice(1);
                setTitle(capitalized);
              }}
              maxLength={20}
              className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm sm:text-base"
              required
            />
          </div>

          {/* Date Input */}
          <div>
            <input
              type="date"
              value={date}
              onChange={(e) => {
                console.log("Date input changed:", e.target.value);
                setDate(e.target.value);
              }}
              className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm sm:text-base appearance-none"
              required
            />
          </div>

          {/* Time Input */}
          <div>
            <input
              type="time"
              value={time}
              onChange={(e) => {
                console.log("Time input changed:", e.target.value);
                setTime(e.target.value);
              }}
              className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm sm:text-base appearance-none"
              required
            />
          </div>

          {/* Description Input with character count */}
          <div>
            <textarea
              placeholder="Enter Event Description (optional)"
              value={description}
              onChange={(e) => {
                const text = e.target.value.slice(0, 100);
                setDescription(text);
              }}
              maxLength={100}
              rows={2}
              className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm sm:text-base resize-none"
            ></textarea>
            <div className="text-xs text-gray-400 text-right mt-1">
              {description.length}/100
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              className="w-full bg-[#D4AF37] text-white py-2 rounded-md hover:bg-[#C4A137] transition-colors text-sm sm:text-base"
              disabled={loading}
            >
              {loading ? "Updating..." : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700 transition-colors text-sm sm:text-base"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditEventModal;
